// src/components/wrestler/ScoreChart.jsx - Versione con Recharts
import React from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Dot
} from 'recharts';

function ScoreChart({
  data,
  compact = false,
  height = compact ? 130 : 180,
  showLabels = !compact,
  maxItems = compact ? 8 : 12,
  average: averageProp
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartPalette = {
    surface: 'linear-gradient(180deg, rgba(18, 24, 31, 0.94), rgba(28, 38, 49, 0.94))',
    border: 'rgba(146, 201, 252, 0.16)',
    grid: 'rgba(146, 201, 252, 0.12)',
    text: '#ffffff',
    muted: '#abc0d7',
    tooltipBg: 'rgba(18, 24, 31, 0.96)',
    tooltipBorder: 'rgba(146, 201, 252, 0.22)',
    statBg: 'rgba(18, 24, 31, 0.88)',
    statBorder: 'rgba(194, 157, 84, 0.22)',
    positive: '#8de26f',
    negative: '#ff8a93',
    line: '#8de26f',
  };

  // Prepara i dati
  const sorted = [...data]
    .filter(d => d.date && d.total !== undefined)
    .sort((a, b) => a.date - b.date);
  
  const lastItems = sorted.slice(-maxItems);
  
  // Formatta per recharts
  const chartData = lastItems.map(item => ({
    date: item.date,
    value: item.total,
    dateStr: item.date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short'
    }),
    formattedDate: item.date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }));

  if (!chartData.length) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: height,
        flexDirection: 'column',
        background: chartPalette.surface,
        borderRadius: 1,
        p: 2,
        border: `1px solid ${chartPalette.border}`
      }}>
        <i className="bi bi-bar-chart-line fs-4 text-muted mb-2"></i>
        <Typography variant="body2" align="center" sx={{ color: chartPalette.muted }}>
          Dati insufficienti per il grafico
        </Typography>
      </Box>
    );
  }

  // Calcola statistiche
  const scores = chartData.map(d => d.value);
  const firstValue = chartData[0]?.value || 0;
  const lastValue = chartData[chartData.length - 1]?.value || 0;
  const difference = lastValue - firstValue;
const average = averageProp !== undefined 
    ? averageProp 
    : scores.reduce((a, b) => a + b, 0) / scores.length;
  // Tooltip personalizzato
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const dateStr = payload[0].payload.dateStr;
      
      return (
        <Box sx={{
          bgcolor: chartPalette.tooltipBg,
          p: 1.5,
          borderRadius: 1,
          border: `1px solid ${chartPalette.tooltipBorder}`,
          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.28)',
          minWidth: 100
        }}>
          <Typography variant="caption" sx={{ 
            fontWeight: 600,
            color: value >= 0 ? chartPalette.positive : chartPalette.negative,
            display: 'block'
          }}>
            {value > 0 ? `+${value}` : value} pt
          </Typography>
          <Typography variant="caption" sx={{ 
            color: chartPalette.muted,
            display: 'block'
          }}>
            {payload[0].payload.formattedDate}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Punto personalizzato per l'ultimo valore
  const renderCustomDot = (props) => {
    const { cx, cy, index, value } = props;
    const isLastPoint = index === chartData.length - 1;
    
    if (isLastPoint) {
      return (
        <Dot
          cx={cx}
          cy={cy}
          r={6}
          fill={value >= 0 ? chartPalette.positive : chartPalette.negative}
          stroke="#12181f"
          strokeWidth={2}
        />
      );
    }
    
    if (index % 2 === 0) { // Mostra solo alcuni punti per evitare sovraffollamento
      return (
        <Dot
          cx={cx}
          cy={cy}
          r={3}
          fill={value >= 0 ? chartPalette.positive : chartPalette.negative}
          stroke="#12181f"
          strokeWidth={1}
        />
      );
    }
    
    return null;
  };

  // Formatta l'asse X per mostrare solo alcune date
  const formatXAxis = (value, index) => {
    if (index === 0 || 
        index === Math.floor(chartData.length / 2) || 
        index === chartData.length - 1) {
      return value;
    }
    return '';
  };

  return (
    <Box sx={{ 
      position: 'relative', 
      height: height,
      background: chartPalette.surface,
      borderRadius: 1,
      p: compact ? 1 : 2,
      border: `1px solid ${chartPalette.border}`,
      overflow: 'hidden'
    }}>
      {/* Titolo e statistiche */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1
      }}>
        <Typography variant="caption" sx={{ 
          fontWeight: 600, 
          color: chartPalette.text,
          fontSize: '0.8rem'
        }}>
          Punteggi
        </Typography>
        
        {/* Statistiche */}
        {chartData.length >= 2 && (
          <Box sx={{
            position: 'absolute',
            bottom: compact ? 5 : 10,
            right: compact ? 5 : 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
            bgcolor: chartPalette.statBg,
            px: 1.5,
            py: 1,
            borderRadius: '6px',
            border: `1px solid ${chartPalette.statBorder}`,
            backdropFilter: 'blur(4px)',
            zIndex: 10
          }}>
            <Typography variant="caption" sx={{ 
              fontSize: '0.7rem',
              color: chartPalette.muted
            }}>
              Media: {average.toFixed(1)}pt
            </Typography>
          </Box>
        )}
      </Box>

      {/* Grafico Recharts */}
      <Box sx={{
        height: `calc(100% - ${compact ? 30 : 40}px)`,
        width: '100%'
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{
              top: 5,
              right: 5,
              left: -20,
              bottom: showLabels ? 20 : 5
            }}
          >
            <CartesianGrid 
              strokeDasharray="2 2" 
              stroke={chartPalette.grid}
              horizontal={true}
              vertical={false}
            />
            
            {/* Linea dello zero se necessario */}
            {Math.min(...scores) < 0 && Math.max(...scores) > 0 && (
              <ReferenceLine 
                y={0} 
                stroke="rgba(255,255,255,0.28)" 
                strokeDasharray="3 3" 
              />
            )}
            
            <XAxis 
              dataKey="dateStr"
              tickFormatter={formatXAxis}
              tick={{
                fontSize: 10,
                fill: chartPalette.muted
              }}
              axisLine={false}
              tickLine={false}
              interval={0}
              minTickGap={1}
            />
            
            <YAxis 
              tick={{
                fontSize: 10,
                fill: chartPalette.muted
              }}
              axisLine={false}
              tickLine={false}
              width={30}
              tickFormatter={(value) => value > 0 ? `+${value}` : value}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* Linea del grafico con area */}
            <Line
              type="monotone"
              dataKey="value"
              stroke={chartPalette.line}
              strokeWidth={2}
              dot={renderCustomDot}
              activeDot={{
                r: 6,
                fill: chartPalette.line,
                stroke: '#12181f',
                strokeWidth: 2
              }}
              strokeLinecap="round"
              fill="url(#colorGradient)"
            />
            
            {/* Gradiente per l'area sotto la linea */}
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop 
                  offset="5%" 
                  stopColor={chartPalette.line} 
                  stopOpacity={0.16}
                />
                <stop 
                  offset="95%" 
                  stopColor={chartPalette.line} 
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {/* Legenda date in basso (se necessario) */}
      {showLabels && chartData.length > 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 1,
          px: 1
        }}>
          <Typography variant="caption" sx={{ 
            fontSize: '0.7rem',
            color: chartPalette.muted
          }}>
            {chartData[0].dateStr}
          </Typography>
          {chartData.length > 1 && (
            <Typography variant="caption" sx={{ 
              fontSize: '0.7rem',
              color: chartPalette.muted
            }}>
              {chartData[Math.floor(chartData.length / 2)].dateStr}
            </Typography>
          )}
          <Typography variant="caption" sx={{ 
            fontSize: '0.7rem',
            color: chartPalette.muted
          }}>
            {chartData[chartData.length - 1].dateStr}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default ScoreChart;
